import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  Label,
  Textarea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Progress,
  Alert,
  AlertDescription
} from '@/components/ui';
import {
  Award,
  Star,
  TrendingUp,
  Target,
  BarChart3,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Calendar,
  Users,
  FileText,
  CheckCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Download,
  Settings,
  MessageSquare,
  BookOpen,
  TrendingDown,
  Activity,
  Zap,
  User
} from 'lucide-react';
import performanceService from '../../services/performanceService';
import employeeService from '../../services/employeeService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

const PerformanceReviews = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // State for different data types
  const [reviews, setReviews] = useState([]);
  const [goals, setGoals] = useState([]);
  const [feedbackRequests, setFeedbackRequests] = useState([]);
  const [pips, setPips] = useState([]);
  const [careerPlans, setCareerPlans] = useState([]);
  const [analytics, setAnalytics] = useState({});
  
  // Dialog states
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showViewReviewDialog, setShowViewReviewDialog] = useState(false);
  const [showEditReviewDialog, setShowEditReviewDialog] = useState(false);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [showViewGoalDialog, setShowViewGoalDialog] = useState(false);
  const [showEditGoalDialog, setShowEditGoalDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showViewFeedbackDialog, setShowViewFeedbackDialog] = useState(false);
  const [showPipDialog, setShowPipDialog] = useState(false);
  const [showViewPipDialog, setShowViewPipDialog] = useState(false);
  const [showEditPipDialog, setShowEditPipDialog] = useState(false);
  const [showCareerDialog, setShowCareerDialog] = useState(false);
  const [showViewCareerDialog, setShowViewCareerDialog] = useState(false);
  const [showEditCareerDialog, setShowEditCareerDialog] = useState(false);
  const [showSkillAssessmentDialog, setShowSkillAssessmentDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [selectedPip, setSelectedPip] = useState(null);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [careerPaths, setCareerPaths] = useState([]);
  
  // Form states
  const [reviewForm, setReviewForm] = useState({
    employee_id: '',
    reviewer_id: '',
    review_type: 'annual',
    review_period_start: '',
    review_period_end: '',
    due_date: ''
  });

  const [editReviewForm, setEditReviewForm] = useState({
    overall_rating: '',
    goals_achievements: '',
    strengths: '',
    areas_for_improvement: '',
    development_plan: '',
    reviewer_comments: ''
  });
  
  const [goalForm, setGoalForm] = useState({
    employee_id: '',
    title: '',
    description: '',
    category: 'performance',
    priority: 'medium',
    target_date: '',
    weight: 100,
    success_criteria: '',
    resources_needed: ''
  });

  const [feedbackForm, setFeedbackForm] = useState({
    employee_id: '',
    title: '',
    description: '',
    feedback_type: '360_review',
    deadline: '',
    anonymous: false,
    reviewers: [],
    instructions: ''
  });

  const [pipForm, setPipForm] = useState({
    employee_id: '',
    manager_id: '',
    hr_partner_id: '',
    title: '',
    description: '',
    performance_issues: '',
    improvement_goals: [],
    success_criteria: '',
    support_resources: '',
    review_schedule: 'weekly',
    duration_months: 3,
    start_date: ''
  });

  const [careerForm, setCareerForm] = useState({
    employee_id: '',
    manager_id: '',
    title: '',
    description: '',
    current_role: '',
    target_role: '',
    career_path_id: '',
    target_timeline: '1_year',
    development_goals: [],
    skill_gaps: [],
    training_needs: '',
    mentoring_requirements: '',
    success_metrics: ''
  });

  const [skillAssessmentForm, setSkillAssessmentForm] = useState({
    employee_id: '',
    assessment_type: 'self_assessment',
    assessment_period: '',
    technical_skills: {},
    soft_skills: {},
    leadership_skills: {},
    assessment_notes: ''
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    department: '',
    date_range: 'current_year'
  });

  useEffect(() => {
    loadDashboardData();
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      // Load employees, departments, and career paths for form dropdowns
      const results = await Promise.allSettled([
        employeeService.getEmployees({ limit: 500 }),
        employeeService.getDepartments(),
        performanceService.getCareerPaths()
      ]);
      
      const [employeesResponse, departmentsResponse, careerPathsResponse] = results;
      
      if (employeesResponse.status === 'fulfilled') {
        setEmployees(employeesResponse.value.data?.data?.employees || employeesResponse.value.data?.employees || []);
      }
      if (departmentsResponse.status === 'fulfilled') {
        setDepartments(departmentsResponse.value.data?.data?.departments || departmentsResponse.value.data?.departments || []);
      }
      if (careerPathsResponse.status === 'fulfilled') {
        setCareerPaths(careerPathsResponse.value.data?.data?.careerPaths || careerPathsResponse.value.data?.careerPaths || []);
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  useEffect(() => {
    if (activeTab !== 'dashboard') {
      loadTabData();
    }
  }, [activeTab, filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        performanceService.getPerformanceAnalytics(),
        performanceService.getReviews({ limit: 5 }),
        performanceService.getGoals({ limit: 5 })
      ]);
      
      const [analyticsResponse, reviewsResponse, goalsResponse] = results;
      
      if (analyticsResponse.status === 'fulfilled') {
        setAnalytics(analyticsResponse.value.data || {});
      }
      if (reviewsResponse.status === 'fulfilled') {
        setReviews(reviewsResponse.value.data?.reviews || reviewsResponse.value.data?.data?.reviews || []);
      }
      if (goalsResponse.status === 'fulfilled') {
        setGoals(goalsResponse.value.data?.goals || goalsResponse.value.data?.data?.goals || []);
      }
    } catch (error) {
      console.error('Dashboard load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTabData = async () => {
    try {
      setLoading(true);
      
      switch (activeTab) {
        case 'reviews': {
          const reviewsResponse = await performanceService.getReviews(filters);
          console.log('Reviews response:', reviewsResponse);
          setReviews(reviewsResponse.data?.data?.reviews || reviewsResponse.data?.reviews || []);
          break;
        }
        case 'goals': {
          const goalsResponse = await performanceService.getGoals(filters);
          console.log('Goals response:', goalsResponse);
          setGoals(goalsResponse.data?.data?.goals || goalsResponse.data?.goals || []);
          break;
        }
        case 'feedback': {
          const feedbackResponse = await performanceService.getFeedbackRequests(filters);
          setFeedbackRequests(feedbackResponse.data?.data?.requests || feedbackResponse.data?.requests || []);
          break;
        }
        case 'pips': {
          const pipsResponse = await performanceService.getPIPs(filters);
          setPips(pipsResponse.data?.data?.pips || pipsResponse.data?.pips || []);
          break;
        }
        case 'career': {
          const careerResponse = await performanceService.getCareerPlans(filters);
          setCareerPlans(careerResponse.data?.data?.plans || careerResponse.data?.plans || []);
          break;
        }
        default:
          break;
      }
    } catch (error) {
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.createReview(reviewForm);
      toast.success('Performance review created successfully');
      setShowReviewDialog(false);
      setReviewForm({
        employee_id: '',
        reviewer_id: '',
        review_type: 'annual',
        review_period_start: '',
        review_period_end: '',
        due_date: ''
      });
      loadTabData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create review');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditReview = (review) => {
    setSelectedReview(review);
    setEditReviewForm({
      overall_rating: review.overall_rating || '',
      goals_achievements: review.goals_achievements || '',
      strengths: review.strengths || '',
      areas_for_improvement: review.areas_for_improvement || '',
      development_plan: review.development_plan || '',
      reviewer_comments: review.reviewer_comments || ''
    });
    setShowEditReviewDialog(true);
  };

  const handleUpdateReview = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.updateReview(selectedReview.id, editReviewForm);
      toast.success('Review updated successfully');
      setShowEditReviewDialog(false);
      loadTabData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update review');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.createGoal(goalForm);
      toast.success('Goal created successfully');
      setShowGoalDialog(false);
      setGoalForm({
        employee_id: '',
        title: '',
        description: '',
        category: 'performance',
        priority: 'medium',
        target_date: '',
        weight: 100,
        success_criteria: '',
        resources_needed: ''
      });
      loadTabData();
    } catch (error) {
      toast.error('Failed to create goal');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFeedback = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.createFeedbackRequest(feedbackForm);
      toast.success('Feedback request created successfully');
      setShowFeedbackDialog(false);
      setFeedbackForm({
        employee_id: '',
        title: '',
        description: '',
        feedback_type: '360_review',
        deadline: '',
        anonymous: false,
        reviewers: [],
        instructions: ''
      });
      loadTabData();
    } catch (error) {
      toast.error('Failed to create feedback request');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePip = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.createPIP(pipForm);
      toast.success('Performance Improvement Plan created successfully');
      setShowPipDialog(false);
      setPipForm({
        employee_id: '',
        manager_id: '',
        hr_partner_id: '',
        title: '',
        description: '',
        performance_issues: '',
        improvement_goals: [],
        success_criteria: '',
        support_resources: '',
        review_schedule: 'weekly',
        duration_months: 3,
        start_date: ''
      });
      loadTabData();
    } catch (error) {
      toast.error('Failed to create PIP');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCareerPlan = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.createCareerPlan(careerForm);
      toast.success('Career Development Plan created successfully');
      setShowCareerDialog(false);
      setCareerForm({
        employee_id: '',
        manager_id: '',
        title: '',
        description: '',
        current_role: '',
        target_role: '',
        career_path_id: '',
        target_timeline: '1_year',
        development_goals: [],
        skill_gaps: [],
        training_needs: '',
        mentoring_requirements: '',
        success_metrics: ''
      });
      loadTabData();
    } catch (error) {
      toast.error('Failed to create career plan');
    } finally {
      setLoading(false);
    }
  };

  // Update handlers
  const handleUpdateGoal = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.updateGoal(selectedGoal.id, {
        progress: parseInt(selectedGoal.progress) || 0,
        status: selectedGoal.status
      });
      toast.success('Goal updated successfully');
      setShowEditGoalDialog(false);
      loadTabData();
    } catch (error) {
      toast.error('Failed to update goal');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePip = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.updatePIP(selectedPip.id, {
        progress: parseInt(selectedPip.progress) || 0,
        status: selectedPip.status
      });
      toast.success('PIP updated successfully');
      setShowEditPipDialog(false);
      loadTabData();
    } catch (error) {
      toast.error('Failed to update PIP');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCareer = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.updateCareerPlan(selectedCareer.id, {
        progress: parseInt(selectedCareer.progress) || 0,
        status: selectedCareer.status
      });
      toast.success('Career plan updated successfully');
      setShowEditCareerDialog(false);
      loadTabData();
    } catch (error) {
      toast.error('Failed to update career plan');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSkillAssessment = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.createSkillAssessment(skillAssessmentForm);
      toast.success('Skill assessment created successfully');
      setShowSkillAssessmentDialog(false);
      setSkillAssessmentForm({
        employee_id: '',
        assessment_type: 'self_assessment',
        assessment_period: '',
        technical_skills: {},
        soft_skills: {},
        leadership_skills: {},
        assessment_notes: ''
      });
      loadTabData();
    } catch (error) {
      toast.error('Failed to create skill assessment');
    } finally {
      setLoading(false);
    }
  };

  const DashboardTab = () => (
    <div className="space-y-6">
      {/* Performance Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Rating</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {analytics.averageRating || '4.2'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.ratingTrend >= 0 ? '+' : ''}{analytics.ratingTrend || '+0.3'} from last period
                </p>
              </div>
              <Star className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Goals Achieved</p>
                <p className="text-2xl font-bold text-green-600">
                  {analytics.goalsAchieved || '85'}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.goalsTotal || '142'} total goals
                </p>
              </div>
              <Target className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Reviews Due</p>
                <p className="text-2xl font-bold text-orange-600">
                  {analytics.reviewsDue || '23'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Next 30 days
                </p>
              </div>
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Team Performance</p>
                <p className="text-2xl font-bold text-blue-600">
                  {analytics.teamPerformance || '92'}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Above expectations
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Recent Reviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reviews.slice(0, 5).map((review) => (
                <div key={review.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {review.employee?.User
                          ? `${review.employee.User.first_name} ${review.employee.User.last_name}`
                          : 'Employee'}
                      </p>
                      <p className="text-sm text-gray-500 capitalize">{review.review_type} Review</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={performanceService.getReviewStatusColor(review.status)}>
                      {review.status?.replace('_', ' ')}
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">
                      Due: {performanceService.formatDate(review.due_date)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Active Goals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {goals.slice(0, 5).map((goal) => (
                <div key={goal.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">{goal.title}</p>
                    <Badge className={performanceService.getGoalStatusColor(goal.status)}>
                      {goal.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{goal.progress || 0}%</span>
                    </div>
                    <Progress value={goal.progress || 0} className="h-2" />
                    <p className="text-xs text-gray-500">
                      Target: {performanceService.formatDate(goal.target_date)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Performance Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { level: 'Exceptional', count: analytics.exceptional || 12, color: 'bg-green-500' },
              { level: 'Exceeds', count: analytics.exceeds || 28, color: 'bg-blue-500' },
              { level: 'Meets', count: analytics.meets || 45, color: 'bg-yellow-500' },
              { level: 'Below', count: analytics.below || 8, color: 'bg-orange-500' },
              { level: 'Needs Improvement', count: analytics.needsImprovement || 3, color: 'bg-red-500' }
            ].map((item) => (
              <div key={item.level} className="text-center">
                <div className={`${item.color} h-20 rounded-lg mb-2 flex items-end justify-center pb-2`}>
                  <span className="text-white font-bold text-lg">{item.count}</span>
                </div>
                <p className="text-sm font-medium">{item.level}</p>
                <p className="text-xs text-gray-500">
                  {Math.round((item.count / 96) * 100)}%
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const ReviewsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Performance Reviews</h2>
          <p className="text-gray-600">Manage employee performance evaluations</p>
        </div>
        <Button onClick={() => setShowReviewDialog(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Review
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search reviews..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            <select 
              value={filters.status} 
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="in_progress">In Progress</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="completed">Completed</option>
            </select>
            <select 
              value={filters.type} 
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="annual">Annual</option>
              <option value="mid_year">Mid-Year</option>
              <option value="quarterly">Quarterly</option>
              <option value="probation">Probation</option>
            </select>
            <Button variant="outline" onClick={loadTabData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reviews Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Reviewer</TableHead>
                <TableHead>Review Type</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No reviews found
                  </TableCell>
                </TableRow>
              ) : (
                reviews.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {review.employee?.User?.first_name} {review.employee?.User?.last_name}
                          </p>
                          <p className="text-sm text-gray-500">{review.employee?.position}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {review.reviewer?.User?.first_name} {review.reviewer?.User?.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{review.reviewer?.position}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{review.review_type?.replace('_', ' ')}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{performanceService.formatDate(review.review_period_start)}</p>
                        <p className="text-gray-500">to {performanceService.formatDate(review.review_period_end)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={performanceService.getReviewStatusColor(review.status)}>
                        {review.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{performanceService.formatDate(review.due_date)}</TableCell>
                    <TableCell>
                      {review.overall_rating ? (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          <span>{review.overall_rating}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">Not rated</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedReview(review);
                            setShowViewReviewDialog(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenEditReview(review)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  const GoalsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Goals & Objectives</h2>
          <p className="text-gray-600">Track and manage employee goals</p>
        </div>
        <Button onClick={() => setShowGoalDialog(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Goal
        </Button>
      </div>

      {/* Goals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {goals.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No goals found. Create your first goal!</p>
          </div>
        ) : (
          goals.map((goal) => (
            <Card key={goal.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{goal.title}</CardTitle>
                  <Badge className={performanceService.getGoalStatusColor(goal.status)}>
                    {goal.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500">
                  {goal.employee?.User?.first_name} {goal.employee?.User?.last_name} - {goal.employee?.position}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">{goal.description}</p>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Progress</span>
                      <span>{goal.progress || 0}%</span>
                    </div>
                    <Progress value={goal.progress || 0} className="h-2" />
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Target Date:</span>
                    <span>{performanceService.formatDate(goal.target_date)}</span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Priority:</span>
                    <Badge variant="outline" className="capitalize">{goal.priority}</Badge>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Weight:</span>
                    <span>{goal.weight}%</span>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => { setSelectedGoal(goal); setShowViewGoalDialog(true); }}>
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => { setSelectedGoal({...goal}); setShowEditGoalDialog(true); }}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  const FeedbackTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">360-Degree Feedback</h2>
          <p className="text-gray-600">Multi-source feedback collection and management</p>
        </div>
        <Button onClick={() => setShowFeedbackDialog(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Request Feedback
        </Button>
      </div>

      {/* Feedback Requests Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {feedbackRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No feedback requests found. Create your first request!
                  </TableCell>
                </TableRow>
              ) : (
                feedbackRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {request.employee?.User?.first_name} {request.employee?.User?.last_name}
                          </p>
                          <p className="text-sm text-gray-500">{request.employee?.position}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{request.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {request.feedback_type?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{performanceService.formatDate(request.deadline)}</TableCell>
                    <TableCell>
                      <Badge className={
                        request.status === 'completed' ? 'bg-green-100 text-green-800' :
                        request.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {request.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedFeedback(request); setShowViewFeedbackDialog(true); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  const PipsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Performance Improvement Plans</h2>
          <p className="text-gray-600">Manage and track performance improvement initiatives</p>
        </div>
        <Button onClick={() => setShowPipDialog(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create PIP
        </Button>
      </div>

      {/* PIPs Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No PIPs found. Create your first Performance Improvement Plan!
                  </TableCell>
                </TableRow>
              ) : (
                pips.map((pip) => (
                  <TableRow key={pip.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {pip.employee?.User?.first_name} {pip.employee?.User?.last_name}
                          </p>
                          <p className="text-sm text-gray-500">{pip.employee?.position}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{pip.title}</TableCell>
                    <TableCell>
                      {pip.manager?.User?.first_name} {pip.manager?.User?.last_name}
                    </TableCell>
                    <TableCell>
                      <div className="w-full">
                        <div className="flex justify-between text-sm mb-1">
                          <span>{pip.progress || 0}%</span>
                        </div>
                        <Progress value={pip.progress || 0} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        pip.status === 'completed' ? 'bg-green-100 text-green-800' :
                        pip.status === 'active' ? 'bg-blue-100 text-blue-800' :
                        pip.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {pip.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{performanceService.formatDate(pip.end_date)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedPip(pip); setShowViewPipDialog(true); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedPip({...pip}); setShowEditPipDialog(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  const CareerTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Career Development Plans</h2>
          <p className="text-gray-600">Employee career growth and development planning</p>
        </div>
        <Button onClick={() => setShowCareerDialog(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create Plan
        </Button>
      </div>

      {/* Career Plans Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Current Role</TableHead>
                <TableHead>Target Role</TableHead>
                <TableHead>Timeline</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {careerPlans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No career plans found. Create your first Career Development Plan!
                  </TableCell>
                </TableRow>
              ) : (
                careerPlans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {plan.employee?.User?.first_name} {plan.employee?.User?.last_name}
                          </p>
                          <p className="text-sm text-gray-500">{plan.employee?.position}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{plan.title}</TableCell>
                    <TableCell>{plan.current_role}</TableCell>
                    <TableCell className="font-medium text-green-600">{plan.target_role}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {plan.target_timeline?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="w-24">
                        <div className="flex justify-between text-sm mb-1">
                          <span>{plan.progress || 0}%</span>
                        </div>
                        <Progress value={plan.progress || 0} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        plan.status === 'completed' ? 'bg-green-100 text-green-800' :
                        plan.status === 'active' ? 'bg-blue-100 text-blue-800' :
                        plan.status === 'on_hold' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {plan.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedCareer(plan); setShowViewCareerDialog(true); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedCareer({...plan}); setShowEditCareerDialog(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Performance Management</h1>
          <p className="text-gray-600">Comprehensive performance review and goal tracking system</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button variant="outline" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Settings
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="reviews" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Reviews
          </TabsTrigger>
          <TabsTrigger value="goals" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Goals
          </TabsTrigger>
          <TabsTrigger value="feedback" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            360 Feedback
          </TabsTrigger>
          <TabsTrigger value="pips" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            PIPs
          </TabsTrigger>
          <TabsTrigger value="career" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Career Plans
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardTab />
        </TabsContent>

        <TabsContent value="reviews">
          <ReviewsTab />
        </TabsContent>

        <TabsContent value="goals">
          <GoalsTab />
        </TabsContent>

        <TabsContent value="feedback">
          <FeedbackTab />
        </TabsContent>

        <TabsContent value="pips">
          <PipsTab />
        </TabsContent>

        <TabsContent value="career">
          <CareerTab />
        </TabsContent>
      </Tabs>

      {/* Create Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Performance Review</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateReview} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="employee">Employee *</Label>
                <select 
                  value={reviewForm.employee_id} 
                  onChange={(e) => setReviewForm(prev => ({ ...prev, employee_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id.toString()}>
                      {employee.User?.first_name || employee.first_name} {employee.User?.last_name || employee.last_name} - {employee.position || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="reviewer">Reviewer *</Label>
                <select 
                  value={reviewForm.reviewer_id} 
                  onChange={(e) => setReviewForm(prev => ({ ...prev, reviewer_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Reviewer</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id.toString()}>
                      {employee.User?.first_name || employee.first_name} {employee.User?.last_name || employee.last_name} - {employee.position || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="review_period_start">Review Period Start *</Label>
                <Input
                  type="date"
                  value={reviewForm.review_period_start}
                  onChange={(e) => setReviewForm(prev => ({ ...prev, review_period_start: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="review_period_end">Review Period End *</Label>
                <Input
                  type="date"
                  value={reviewForm.review_period_end}
                  onChange={(e) => setReviewForm(prev => ({ ...prev, review_period_end: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="review_type">Review Type *</Label>
                <select 
                  value={reviewForm.review_type} 
                  onChange={(e) => setReviewForm(prev => ({ ...prev, review_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="annual">Annual Review</option>
                  <option value="mid_year">Mid-Year Review</option>
                  <option value="quarterly">Quarterly Review</option>
                  <option value="probation">Probation Review</option>
                </select>
              </div>
              <div>
                <Label htmlFor="due_date">Due Date *</Label>
                <Input
                  type="date"
                  value={reviewForm.due_date}
                  onChange={(e) => setReviewForm(prev => ({ ...prev, due_date: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowReviewDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Review'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Goal Dialog */}
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Goal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateGoal} className="space-y-4">
            <div>
              <Label htmlFor="employee">Employee *</Label>
              <select 
                value={goalForm.employee_id} 
                onChange={(e) => setGoalForm(prev => ({ ...prev, employee_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Employee</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id.toString()}>
                    {employee.User?.first_name || employee.first_name} {employee.User?.last_name || employee.last_name} - {employee.position || 'N/A'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="title">Goal Title *</Label>
              <Input
                value={goalForm.title}
                onChange={(e) => setGoalForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter goal title"
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                value={goalForm.description}
                onChange={(e) => setGoalForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the goal"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <select 
                  value={goalForm.category} 
                  onChange={(e) => setGoalForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="performance">Performance</option>
                  <option value="development">Development</option>
                  <option value="behavioral">Behavioral</option>
                  <option value="project">Project</option>
                </select>
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <select 
                  value={goalForm.priority} 
                  onChange={(e) => setGoalForm(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <Label htmlFor="weight">Weight (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={goalForm.weight}
                  onChange={(e) => setGoalForm(prev => ({ ...prev, weight: parseInt(e.target.value) }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="target_date">Target Date *</Label>
              <Input
                type="date"
                value={goalForm.target_date}
                onChange={(e) => setGoalForm(prev => ({ ...prev, target_date: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="success_criteria">Success Criteria</Label>
              <Textarea
                value={goalForm.success_criteria}
                onChange={(e) => setGoalForm(prev => ({ ...prev, success_criteria: e.target.value }))}
                placeholder="Define what success looks like for this goal"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="resources_needed">Resources Needed</Label>
              <Textarea
                value={goalForm.resources_needed}
                onChange={(e) => setGoalForm(prev => ({ ...prev, resources_needed: e.target.value }))}
                placeholder="List any resources, training, or support needed"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowGoalDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Goal'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Feedback Request Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Request 360-Degree Feedback</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFeedback} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="employee">Employee *</Label>
                <select 
                  value={feedbackForm.employee_id} 
                  onChange={(e) => setFeedbackForm(prev => ({ ...prev, employee_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id.toString()}>
                      {employee.User?.first_name || employee.first_name} {employee.User?.last_name || employee.last_name} - {employee.position || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="feedback_type">Feedback Type *</Label>
                <select 
                  value={feedbackForm.feedback_type} 
                  onChange={(e) => setFeedbackForm(prev => ({ ...prev, feedback_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="360_review">360-Degree Review</option>
                  <option value="peer_feedback">Peer Feedback</option>
                  <option value="upward_feedback">Upward Feedback</option>
                  <option value="manager_feedback">Manager Feedback</option>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="title">Request Title *</Label>
              <Input
                value={feedbackForm.title}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter feedback request title"
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                value={feedbackForm.description}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the purpose and context of this feedback request"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="deadline">Response Deadline *</Label>
                <Input
                  type="date"
                  value={feedbackForm.deadline}
                  onChange={(e) => setFeedbackForm(prev => ({ ...prev, deadline: e.target.value }))}
                  required
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={feedbackForm.anonymous}
                  onChange={(e) => setFeedbackForm(prev => ({ ...prev, anonymous: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="anonymous">Allow anonymous feedback</Label>
              </div>
            </div>
            <div>
              <Label htmlFor="instructions">Instructions for Reviewers</Label>
              <Textarea
                value={feedbackForm.instructions}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, instructions: e.target.value }))}
                placeholder="Provide specific instructions or focus areas for reviewers"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowFeedbackDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create PIP Dialog */}
      <Dialog open={showPipDialog} onOpenChange={setShowPipDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Performance Improvement Plan</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePip} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="employee">Employee *</Label>
                <select 
                  value={pipForm.employee_id} 
                  onChange={(e) => setPipForm(prev => ({ ...prev, employee_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id.toString()}>
                      {employee.User?.first_name || employee.first_name} {employee.User?.last_name || employee.last_name} - {employee.position || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="manager">Manager *</Label>
                <select 
                  value={pipForm.manager_id} 
                  onChange={(e) => setPipForm(prev => ({ ...prev, manager_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Manager</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id.toString()}>
                      {employee.User?.first_name || employee.first_name} {employee.User?.last_name || employee.last_name} - {employee.position || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="hr_partner">HR Partner</Label>
                <select 
                  value={pipForm.hr_partner_id} 
                  onChange={(e) => setPipForm(prev => ({ ...prev, hr_partner_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select HR Partner (Optional)</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id.toString()}>
                      {employee.User?.first_name || employee.first_name} {employee.User?.last_name || employee.last_name} - {employee.position || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="title">PIP Title *</Label>
              <Input
                value={pipForm.title}
                onChange={(e) => setPipForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter PIP title"
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                value={pipForm.description}
                onChange={(e) => setPipForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Provide an overview of the performance improvement plan"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="performance_issues">Performance Issues *</Label>
              <Textarea
                value={pipForm.performance_issues}
                onChange={(e) => setPipForm(prev => ({ ...prev, performance_issues: e.target.value }))}
                placeholder="Describe the specific performance issues that need to be addressed"
                rows={3}
                required
              />
            </div>
            <div>
              <Label htmlFor="success_criteria">Success Criteria *</Label>
              <Textarea
                value={pipForm.success_criteria}
                onChange={(e) => setPipForm(prev => ({ ...prev, success_criteria: e.target.value }))}
                placeholder="Define clear, measurable criteria for successful completion"
                rows={3}
                required
              />
            </div>
            <div>
              <Label htmlFor="improvement_goals">Improvement Goals * (one per line)</Label>
              <Textarea
                value={pipForm.improvement_goals.join('\n')}
                onChange={(e) => setPipForm(prev => ({ 
                  ...prev, 
                  improvement_goals: e.target.value.split('\n').filter(g => g.trim()) 
                }))}
                placeholder="Enter improvement goals, one per line:&#10;- Improve attendance to 95%&#10;- Complete required training&#10;- Meet weekly targets"
                rows={4}
                required
              />
            </div>
            <div>
              <Label htmlFor="support_resources">Support Resources</Label>
              <Textarea
                value={pipForm.support_resources}
                onChange={(e) => setPipForm(prev => ({ ...prev, support_resources: e.target.value }))}
                placeholder="List training, mentoring, or other support resources to be provided"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="review_schedule">Review Schedule *</Label>
                <select 
                  value={pipForm.review_schedule} 
                  onChange={(e) => setPipForm(prev => ({ ...prev, review_schedule: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="weekly">Weekly</option>
                  <option value="bi_weekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <Label htmlFor="duration">Duration (Months) *</Label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={pipForm.duration_months}
                  onChange={(e) => setPipForm(prev => ({ ...prev, duration_months: parseInt(e.target.value) }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  type="date"
                  value={pipForm.start_date}
                  onChange={(e) => setPipForm(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowPipDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create PIP'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Career Development Plan Dialog */}
      <Dialog open={showCareerDialog} onOpenChange={setShowCareerDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Career Development Plan</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCareerPlan} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="employee">Employee *</Label>
                <select 
                  value={careerForm.employee_id} 
                  onChange={(e) => setCareerForm(prev => ({ ...prev, employee_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id.toString()}>
                      {employee.User?.first_name || employee.first_name} {employee.User?.last_name || employee.last_name} - {employee.position || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="manager">Manager</Label>
                <select 
                  value={careerForm.manager_id} 
                  onChange={(e) => setCareerForm(prev => ({ ...prev, manager_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Manager (Optional)</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id.toString()}>
                      {employee.User?.first_name || employee.first_name} {employee.User?.last_name || employee.last_name} - {employee.position || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="title">Plan Title *</Label>
              <Input
                value={careerForm.title}
                onChange={(e) => setCareerForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter career development plan title"
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                value={careerForm.description}
                onChange={(e) => setCareerForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the career development objectives"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="current_role">Current Role *</Label>
                <Input
                  value={careerForm.current_role}
                  onChange={(e) => setCareerForm(prev => ({ ...prev, current_role: e.target.value }))}
                  placeholder="Current position/role"
                  required
                />
              </div>
              <div>
                <Label htmlFor="target_role">Target Role *</Label>
                <Input
                  value={careerForm.target_role}
                  onChange={(e) => setCareerForm(prev => ({ ...prev, target_role: e.target.value }))}
                  placeholder="Desired future position/role"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="career_path">Career Path</Label>
                <select 
                  value={careerForm.career_path_id} 
                  onChange={(e) => setCareerForm(prev => ({ ...prev, career_path_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Career Path</option>
                  {careerPaths.map((path) => (
                    <option key={path.id} value={path.id.toString()}>
                      {path.title} - {path.function_area || 'General'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="timeline">Target Timeline *</Label>
                <select 
                  value={careerForm.target_timeline} 
                  onChange={(e) => setCareerForm(prev => ({ ...prev, target_timeline: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="6_months">6 Months</option>
                  <option value="1_year">1 Year</option>
                  <option value="2_years">2 Years</option>
                  <option value="3_years">3 Years</option>
                  <option value="5_years">5 Years</option>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="training_needs">Training Needs</Label>
              <Textarea
                value={careerForm.training_needs}
                onChange={(e) => setCareerForm(prev => ({ ...prev, training_needs: e.target.value }))}
                placeholder="Identify specific training programs, courses, or certifications needed"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="mentoring_requirements">Mentoring Requirements</Label>
              <Textarea
                value={careerForm.mentoring_requirements}
                onChange={(e) => setCareerForm(prev => ({ ...prev, mentoring_requirements: e.target.value }))}
                placeholder="Describe mentoring or coaching needs"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="success_metrics">Success Metrics</Label>
              <Textarea
                value={careerForm.success_metrics}
                onChange={(e) => setCareerForm(prev => ({ ...prev, success_metrics: e.target.value }))}
                placeholder="Define how success will be measured"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCareerDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Plan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Review Dialog */}
      <Dialog open={showViewReviewDialog} onOpenChange={setShowViewReviewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Performance Review Details</DialogTitle>
          </DialogHeader>
          {selectedReview && (
            <div className="space-y-6">
              {/* Employee & Reviewer Info */}
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-500">Employee Being Reviewed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold">
                          {selectedReview.employee?.User?.first_name} {selectedReview.employee?.User?.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{selectedReview.employee?.position}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-500">Reviewer</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold">
                          {selectedReview.reviewer?.User?.first_name} {selectedReview.reviewer?.User?.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{selectedReview.reviewer?.position}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Review Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Review Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Review Type</p>
                      <p className="font-medium capitalize">{selectedReview.review_type?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <Badge className={performanceService.getReviewStatusColor(selectedReview.status)}>
                        {selectedReview.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Due Date</p>
                      <p className="font-medium">{performanceService.formatDate(selectedReview.due_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Overall Rating</p>
                      {selectedReview.overall_rating ? (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          <span className="font-medium">{selectedReview.overall_rating}/5</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">Not rated yet</span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
                    <div>
                      <p className="text-sm text-gray-500">Review Period Start</p>
                      <p className="font-medium">{performanceService.formatDate(selectedReview.review_period_start)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Review Period End</p>
                      <p className="font-medium">{performanceService.formatDate(selectedReview.review_period_end)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Review Content */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Review Report</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Goals & Achievements</p>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {selectedReview.goals_achievements || 'Not provided yet'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Strengths</p>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {selectedReview.strengths || 'Not provided yet'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Areas for Improvement</p>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {selectedReview.areas_for_improvement || 'Not provided yet'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Development Plan</p>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {selectedReview.development_plan || 'Not provided yet'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Reviewer Comments</p>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {selectedReview.reviewer_comments || 'Not provided yet'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Employee Comments</p>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {selectedReview.employee_comments || 'Not provided yet'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowViewReviewDialog(false)}>
                  Close
                </Button>
                <Button onClick={() => {
                  setShowViewReviewDialog(false);
                  handleOpenEditReview(selectedReview);
                }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Review
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Review Dialog */}
      <Dialog open={showEditReviewDialog} onOpenChange={setShowEditReviewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Performance Review</DialogTitle>
          </DialogHeader>
          {selectedReview && (
            <form onSubmit={handleUpdateReview} className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  Employee: <strong>{selectedReview.employee?.User?.first_name} {selectedReview.employee?.User?.last_name}</strong> - {selectedReview.employee?.position}
                </p>
                <p className="text-sm text-blue-800">
                  Reviewer: <strong>{selectedReview.reviewer?.User?.first_name} {selectedReview.reviewer?.User?.last_name}</strong>
                </p>
              </div>
              <div>
                <Label>Overall Rating (1-5)</Label>
                <select
                  value={editReviewForm.overall_rating}
                  onChange={(e) => setEditReviewForm(prev => ({ ...prev, overall_rating: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select Rating</option>
                  <option value="1">1 - Needs Improvement</option>
                  <option value="2">2 - Below Expectations</option>
                  <option value="3">3 - Meets Expectations</option>
                  <option value="4">4 - Exceeds Expectations</option>
                  <option value="5">5 - Outstanding</option>
                </select>
              </div>
              <div>
                <Label>Goals & Achievements</Label>
                <Textarea
                  value={editReviewForm.goals_achievements}
                  onChange={(e) => setEditReviewForm(prev => ({ ...prev, goals_achievements: e.target.value }))}
                  placeholder="Describe goal achievements during this period..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Strengths</Label>
                <Textarea
                  value={editReviewForm.strengths}
                  onChange={(e) => setEditReviewForm(prev => ({ ...prev, strengths: e.target.value }))}
                  placeholder="List key strengths..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Areas for Improvement</Label>
                <Textarea
                  value={editReviewForm.areas_for_improvement}
                  onChange={(e) => setEditReviewForm(prev => ({ ...prev, areas_for_improvement: e.target.value }))}
                  placeholder="Identify areas for improvement..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Development Plan</Label>
                <Textarea
                  value={editReviewForm.development_plan}
                  onChange={(e) => setEditReviewForm(prev => ({ ...prev, development_plan: e.target.value }))}
                  placeholder="Suggest development activities..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Reviewer Comments</Label>
                <Textarea
                  value={editReviewForm.reviewer_comments}
                  onChange={(e) => setEditReviewForm(prev => ({ ...prev, reviewer_comments: e.target.value }))}
                  placeholder="Additional comments..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowEditReviewDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Review'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* View Goal Dialog */}
      <Dialog open={showViewGoalDialog} onOpenChange={setShowViewGoalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Goal Details</DialogTitle>
          </DialogHeader>
          {selectedGoal && (
            <div className="space-y-4">
              <div><p className="text-sm text-gray-500">Employee</p><p className="font-medium">{selectedGoal.employee?.User?.first_name} {selectedGoal.employee?.User?.last_name}</p></div>
              <div><p className="text-sm text-gray-500">Title</p><p className="font-medium">{selectedGoal.title}</p></div>
              <div><p className="text-sm text-gray-500">Description</p><p>{selectedGoal.description || 'N/A'}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">Category</p><p className="capitalize">{selectedGoal.category}</p></div>
                <div><p className="text-sm text-gray-500">Priority</p><p className="capitalize">{selectedGoal.priority}</p></div>
                <div><p className="text-sm text-gray-500">Progress</p><p>{selectedGoal.progress || 0}%</p></div>
                <div><p className="text-sm text-gray-500">Status</p><p className="capitalize">{selectedGoal.status?.replace('_', ' ')}</p></div>
                <div><p className="text-sm text-gray-500">Target Date</p><p>{performanceService.formatDate(selectedGoal.target_date)}</p></div>
                <div><p className="text-sm text-gray-500">Weight</p><p>{selectedGoal.weight}%</p></div>
              </div>
              <Button className="w-full" onClick={() => setShowViewGoalDialog(false)}>Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={showEditGoalDialog} onOpenChange={setShowEditGoalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Goal Progress</DialogTitle>
          </DialogHeader>
          {selectedGoal && (
            <form onSubmit={handleUpdateGoal} className="space-y-4">
              <div className="bg-blue-50 p-3 rounded"><p className="text-sm">Goal: <strong>{selectedGoal.title}</strong></p></div>
              <div>
                <Label>Progress (%)</Label>
                <Input type="number" min="0" max="100" value={selectedGoal.progress || 0} onChange={(e) => setSelectedGoal({...selectedGoal, progress: e.target.value})} />
              </div>
              <div>
                <Label>Status</Label>
                <select value={selectedGoal.status} onChange={(e) => setSelectedGoal({...selectedGoal, status: e.target.value})} className="w-full px-3 py-2 border rounded-md">
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowEditGoalDialog(false)}>Cancel</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Update'}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* View Feedback Dialog */}
      <Dialog open={showViewFeedbackDialog} onOpenChange={setShowViewFeedbackDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Feedback Request Details</DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <div className="space-y-4">
              <div><p className="text-sm text-gray-500">Employee</p><p className="font-medium">{selectedFeedback.employee?.User?.first_name} {selectedFeedback.employee?.User?.last_name}</p></div>
              <div><p className="text-sm text-gray-500">Title</p><p className="font-medium">{selectedFeedback.title}</p></div>
              <div><p className="text-sm text-gray-500">Description</p><p>{selectedFeedback.description || 'N/A'}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">Type</p><p className="capitalize">{selectedFeedback.feedback_type?.replace('_', ' ')}</p></div>
                <div><p className="text-sm text-gray-500">Status</p><p className="capitalize">{selectedFeedback.status?.replace('_', ' ')}</p></div>
                <div><p className="text-sm text-gray-500">Deadline</p><p>{performanceService.formatDate(selectedFeedback.deadline)}</p></div>
                <div><p className="text-sm text-gray-500">Anonymous</p><p>{selectedFeedback.anonymous ? 'Yes' : 'No'}</p></div>
              </div>
              <Button className="w-full" onClick={() => setShowViewFeedbackDialog(false)}>Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View PIP Dialog */}
      <Dialog open={showViewPipDialog} onOpenChange={setShowViewPipDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>PIP Details</DialogTitle>
          </DialogHeader>
          {selectedPip && (
            <div className="space-y-4">
              <div><p className="text-sm text-gray-500">Employee</p><p className="font-medium">{selectedPip.employee?.User?.first_name} {selectedPip.employee?.User?.last_name}</p></div>
              <div><p className="text-sm text-gray-500">Title</p><p className="font-medium">{selectedPip.title}</p></div>
              <div><p className="text-sm text-gray-500">Performance Issues</p><p>{selectedPip.performance_issues || 'N/A'}</p></div>
              <div><p className="text-sm text-gray-500">Success Criteria</p><p>{selectedPip.success_criteria || 'N/A'}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">Progress</p><p>{selectedPip.progress || 0}%</p></div>
                <div><p className="text-sm text-gray-500">Status</p><p className="capitalize">{selectedPip.status?.replace('_', ' ')}</p></div>
                <div><p className="text-sm text-gray-500">Start Date</p><p>{performanceService.formatDate(selectedPip.start_date)}</p></div>
                <div><p className="text-sm text-gray-500">End Date</p><p>{performanceService.formatDate(selectedPip.end_date)}</p></div>
              </div>
              <Button className="w-full" onClick={() => setShowViewPipDialog(false)}>Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit PIP Dialog */}
      <Dialog open={showEditPipDialog} onOpenChange={setShowEditPipDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update PIP Progress</DialogTitle>
          </DialogHeader>
          {selectedPip && (
            <form onSubmit={handleUpdatePip} className="space-y-4">
              <div className="bg-red-50 p-3 rounded"><p className="text-sm">PIP: <strong>{selectedPip.title}</strong></p></div>
              <div>
                <Label>Progress (%)</Label>
                <Input type="number" min="0" max="100" value={selectedPip.progress || 0} onChange={(e) => setSelectedPip({...selectedPip, progress: e.target.value})} />
              </div>
              <div>
                <Label>Status</Label>
                <select value={selectedPip.status} onChange={(e) => setSelectedPip({...selectedPip, status: e.target.value})} className="w-full px-3 py-2 border rounded-md">
                  <option value="active">Active</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="extended">Extended</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowEditPipDialog(false)}>Cancel</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Update'}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* View Career Dialog */}
      <Dialog open={showViewCareerDialog} onOpenChange={setShowViewCareerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Career Plan Details</DialogTitle>
          </DialogHeader>
          {selectedCareer && (
            <div className="space-y-4">
              <div><p className="text-sm text-gray-500">Employee</p><p className="font-medium">{selectedCareer.employee?.User?.first_name} {selectedCareer.employee?.User?.last_name}</p></div>
              <div><p className="text-sm text-gray-500">Title</p><p className="font-medium">{selectedCareer.title}</p></div>
              <div><p className="text-sm text-gray-500">Description</p><p>{selectedCareer.description || 'N/A'}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">Current Role</p><p>{selectedCareer.current_role}</p></div>
                <div><p className="text-sm text-gray-500">Target Role</p><p className="text-green-600 font-medium">{selectedCareer.target_role}</p></div>
                <div><p className="text-sm text-gray-500">Timeline</p><p className="capitalize">{selectedCareer.target_timeline?.replace('_', ' ')}</p></div>
                <div><p className="text-sm text-gray-500">Progress</p><p>{selectedCareer.progress || 0}%</p></div>
                <div><p className="text-sm text-gray-500">Status</p><p className="capitalize">{selectedCareer.status?.replace('_', ' ')}</p></div>
                <div><p className="text-sm text-gray-500">Target Date</p><p>{performanceService.formatDate(selectedCareer.target_completion_date)}</p></div>
              </div>
              <Button className="w-full" onClick={() => setShowViewCareerDialog(false)}>Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Career Dialog */}
      <Dialog open={showEditCareerDialog} onOpenChange={setShowEditCareerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Career Plan Progress</DialogTitle>
          </DialogHeader>
          {selectedCareer && (
            <form onSubmit={handleUpdateCareer} className="space-y-4">
              <div className="bg-green-50 p-3 rounded"><p className="text-sm">Plan: <strong>{selectedCareer.title}</strong></p></div>
              <div>
                <Label>Progress (%)</Label>
                <Input type="number" min="0" max="100" value={selectedCareer.progress || 0} onChange={(e) => setSelectedCareer({...selectedCareer, progress: e.target.value})} />
              </div>
              <div>
                <Label>Status</Label>
                <select value={selectedCareer.status} onChange={(e) => setSelectedCareer({...selectedCareer, status: e.target.value})} className="w-full px-3 py-2 border rounded-md">
                  <option value="active">Active</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowEditCareerDialog(false)}>Cancel</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Update'}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformanceReviews;